"use client";

import { publishEvent } from "@/events";
import { EventType, type ICustomEvent } from "@/events/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface LanguageSelectionPopupProps {
  onLanguageSelect?: (language: string) => void;
}

export default function LanguageSelectionPopup({
  onLanguageSelect,
}: LanguageSelectionPopupProps) {
  const [isVisible, setIsVisible] = useState(false);
  const { i18n } = useTranslation();

  useEffect(() => {
    // Check if user has already made a language selection
    const hasSelectedLanguage = localStorage.getItem("language-selected");
    if (!hasSelectedLanguage) {
      setIsVisible(true);
    }
  }, []);

  const handleLanguageSelection = (
    language: string,
    habit: "smoke" | "chew",
  ) => {
    // Track habit selection event
    const event: ICustomEvent = {
      type: EventType.CUSTOM,
      name: `${habit}_click`,
      properties: {
        language,
        location: "language_selection_popup",
      },
    };
    publishEvent(event);

    // Save language preference
    localStorage.setItem("preferred-language", language);
    localStorage.setItem("language-selected", "true");

    // Change language
    i18n.changeLanguage(language);

    // Call parent callback if provided
    onLanguageSelect?.(language);

    // Hide popup
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="bg-white rounded-3xl p-6 md:p-8 w-full max-w-2xl mx-4 text-center shadow-2xl">
        {/* English Text */}
        <h2 className="text-lg md:text-2xl lg:text-3xl font-bold text-black mb-3 md:mb-4 leading-tight">
          Which habit are you trying to quit?
        </h2>

        {/* Hindi Text */}
        <p className="text-base md:text-xl lg:text-2xl text-black mb-6 md:mb-8 font-medium">
          आप कौन सी आदत छोड़ने की कोशिश कर रहे हैं?
        </p>

        {/* Buttons Container with red background like in image */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-3xl p-4 md:p-6">
          {/* Desktop: Side by side, Mobile: Stacked */}
          <div className="flex flex-col md:flex-row gap-4 md:gap-6">
            {/* Smoke Button - English */}
            <button
              onClick={() => handleLanguageSelection("en", "smoke")}
              data-test-id="I SMOKE button"
              className="flex-1 bg-white text-black py-4 px-6 rounded-full text-lg md:text-xl font-bold hover:bg-gradient-to-r hover:from-gray-800 hover:to-gray-900 hover:text-white hover:shadow-xl hover:scale-105 transition-all duration-300 ease-in-out shadow-lg transform active:scale-95"
            >
              I SMOKE/स्मोक
            </button>

            {/* Chew Button - Hindi */}
            <button
              onClick={() => handleLanguageSelection("hi", "chew")}
              data-test-id="I CHEW  button"
              className="flex-1 bg-white text-black py-4 px-6 rounded-full text-lg md:text-xl font-bold hover:bg-gradient-to-r hover:from-gray-800 hover:to-gray-900 hover:text-white hover:shadow-xl hover:scale-105 transition-all duration-300 ease-in-out shadow-lg transform active:scale-95"
            >
              I CHEW/मसाला चबाना
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
